import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { MessageCircle, X, Send, Loader2, Sparkles, ArrowLeft, BarChart3, MessageSquare, Calendar, Dumbbell, Zap, Activity, Target, Moon, Brain, Heart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Therapy recommendations by goal
const GOAL_RECS = {
  'weight-loss': {
    label: 'Weight Loss',
    primary: { name: 'Tirzepatide', why: 'Strongest average weight loss efficacy — dual GLP-1/GIP mechanism.' },
    additional: [
      { name: 'Semaglutide', note: 'Proven GLP-1 with broad availability' },
      { name: 'Citradine', note: 'Compounded weight loss support' },
      { name: 'SatiaLean', note: 'Appetite and satiety optimization' },
    ],
    followUp: 'Are you also dealing with low energy, poor sleep, or muscle loss alongside your weight goals?',
  },
  energy: {
    label: 'Energy',
    primary: { name: 'NAD+', why: 'Directly fuels mitochondrial energy production at the cellular level.' },
    additional: [
      { name: 'MOTS-c', note: 'Mitochondrial peptide that upgrades metabolic efficiency' },
      { name: 'Glutathione', note: 'Master antioxidant — reduces oxidative fatigue' },
      { name: 'Lipo-B', note: 'B12-based metabolic and liver support' },
    ],
    followUp: 'Is the fatigue mostly physical, mental (brain fog), or both?',
  },
  recovery: {
    label: 'Recovery',
    hasSubFlow: true,
    subQuestion: "Is this mostly one specific area, or more of a full-body recovery issue?",
    subOptions: [
      { id: 'localized', label: 'One specific area', rec: {
        primary: { name: 'BPC-157', why: 'Targeted recovery support for a specific area of concern — joint, tendon, muscle, or soft tissue.' },
        additional: [{ name: 'Wolverine Stack', note: 'If recovery needs expand, the BPC-157 + TB-500 blend covers broader support' }],
        bryanLine: "Because your concern sounds localized, Bryan would probably start by looking at targeted BPC-157 support first. If recovery needs are broader or multiple areas are involved, the Wolverine Stack may be a better fit.",
      }},
      { id: 'fullbody', label: 'Multiple areas / full-body recovery', rec: {
        primary: { name: 'Wolverine Stack', why: 'Advanced BPC-157 + TB-500 blend designed for comprehensive, multi-area recovery support.' },
        additional: [{ name: 'NAD+', note: 'Cellular energy to support the body\'s natural recovery processes' }],
        bryanLine: "Because your recovery goal sounds broader, Bryan would probably start with the Wolverine Stack — a BPC-157 + TB-500 blend designed for more comprehensive recovery support.",
      }},
      { id: 'athletic', label: 'Gym soreness or athletic recovery', rec: {
        primary: { name: 'Wolverine Stack', why: 'BPC-157 + TB-500 blend — designed for active adults and athletes with ongoing recovery demands.' },
        additional: [{ name: 'BPC-157', note: 'Can also be explored as a targeted option for one specific trouble spot' }],
        bryanLine: "For athletic recovery, Bryan would probably start with the Wolverine Stack — a BPC-157 + TB-500 blend. If there's one specific trouble spot, standalone BPC-157 is also an option.",
      }},
      { id: 'unsure', label: 'Not sure', rec: {
        primary: { name: 'BPC-157', why: 'Targeted recovery support — a focused option for a specific area of concern.' },
        additional: [{ name: 'Wolverine Stack', note: 'BPC-157 + TB-500 blend for broader, multi-area recovery' }],
        bryanLine: "BPC-157 is usually the targeted option for one main area. The Wolverine Stack (BPC-157 + TB-500) is the broader recovery blend. Bryan can help you figure out which fits best.",
      }},
    ],
    primary: { name: 'BPC-157', why: 'Targeted recovery support for specific areas of concern.' },
    additional: [
      { name: 'Wolverine Stack', note: 'BPC-157 + TB-500 for broader, multi-area recovery' },
    ],
    followUp: 'Is this mostly one specific area, or more of a full-body recovery issue?',
  },
  muscle: {
    label: 'Body Composition',
    primary: { name: 'Tesamorelin', why: 'Targets visceral belly fat specifically through natural growth hormone stimulation.' },
    additional: [
      { name: 'Sermorelin', note: 'Broader GH restoration — sleep, muscle, vitality' },
    ],
    followUp: 'Are you also trying to lose weight, or mainly focused on building/preserving muscle?',
  },
  sleep: {
    label: 'Sleep',
    primary: { name: 'Sermorelin', why: 'Growth hormone-releasing peptide — enhances deep sleep and overnight GH production for restorative recovery.' },
    additional: [
      { name: 'NAD+', note: 'Supports circadian rhythm regulation and cellular energy for sleep-wake cycles' },
    ],
    followUp: 'Is the issue falling asleep, staying asleep, or waking up unrefreshed?',
  },
  cognitive: {
    label: 'Focus',
    primary: { name: 'NAD+', why: 'Fuels mitochondrial energy production — supports mental clarity, focus, and reduces brain fog.' },
    additional: [
      { name: 'MOTS-c', note: 'Mitochondrial peptide that supports cognitive energy and metabolic efficiency' },
      { name: 'Glutathione', note: 'Reduces oxidative stress that contributes to mental fatigue' },
    ],
    followUp: 'Is the brain fog constant, or mainly in the afternoon?',
  },
  hormones: {
    label: 'Hormones',
    primary: { name: "Women's HRT", why: 'Personalized hormone support for perimenopause and menopause.' },
    additional: [
      { name: 'Sermorelin', note: 'GH restoration for sleep, recovery, and vitality' },
      { name: 'NAD+', note: 'Cellular energy support during hormonal shifts' },
    ],
    followUp: 'What symptoms are most affecting you — sleep, energy, mood, or weight?',
  },
  intimacy: {
    label: 'Intimacy & Vitality',
    primary: { name: 'PT-141', why: 'Works in the brain to restore desire — not just blood flow.' },
    additional: [
      { name: "Women's HRT", note: 'Hormone optimization for overall wellness' },
      { name: 'Minoxalune', note: 'Additional vitality support' },
    ],
    followUp: 'Is this something that changed recently, or has it been gradual over time?',
  },
};

const GOAL_BUTTONS = [
  { id: 'weight-loss', icon: Dumbbell, label: 'Lose Weight' },
  { id: 'energy', icon: Zap, label: 'Increase Energy' },
  { id: 'recovery', icon: Activity, label: 'Recover Faster' },
  { id: 'muscle', icon: Target, label: 'Improve Body Composition' },
  { id: 'sleep', icon: Moon, label: 'Sleep Better' },
  { id: 'cognitive', icon: Brain, label: 'Improve Focus' },
  { id: 'hormones', icon: Heart, label: "Women's Hormone Health" },
];

const ChatAssistant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatPhase, setChatPhase] = useState('welcome'); // welcome, followup, recommendation, freetext
  const [selectedGoal, setSelectedGoal] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getWelcomeMessage = () => ({
    role: 'assistant',
    content: "Welcome to your AI Health Strategist.\n\nMost people start with one goal. Which sounds most like you?",
    goalButtons: true,
  });

  useEffect(() => {
    setMessages([getWelcomeMessage()]);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!hasAutoOpened && !location.pathname.includes("/quiz") && !location.pathname.includes("/forecast") && !location.pathname.includes("/assessment")) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasAutoOpened(true);
      }, 9000);
      return () => clearTimeout(timer);
    }
  }, [hasAutoOpened, location.pathname]);

  if (
    location.pathname.includes("/quiz") ||
    location.pathname.includes("/forecast") ||
    location.pathname.includes("/assessment") ||
    location.pathname.includes("/admin") ||
    location.pathname.includes("/blog")
  ) {
    return null;
  }

  const handleGoalSelect = (goalId) => {
    const rec = GOAL_RECS[goalId];
    if (!rec) return;

    setSelectedGoal(goalId);

    // User message
    setMessages(prev => [...prev, { role: 'user', content: rec.label }]);

    // Check if this goal has a sub-flow (recovery)
    if (rec.hasSubFlow) {
      setChatPhase('subflow');
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: rec.subQuestion,
          subOptions: rec.subOptions,
        }]);
      }, 600);
      return;
    }

    setChatPhase('recommendation');

    // Build recommendation message
    const additionalList = rec.additional.map(a => `• **${a.name}** — ${a.note}`).join('\n');

    const recMessage = `Based on what you've told me, Bryan would probably start here:\n\n**Primary Recommendation**\n**${rec.primary.name}**\n\n_Why:_ ${rec.primary.why}\n\n**Additional options:**\n${additionalList}\n\nWant me to narrow that down?`;

    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: recMessage,
        quickReplies: [
          { text: "Tell me more", action: 'followup' },
          { text: "What about cost?", action: 'cost' },
        ],
      }]);
    }, 600);
  };

  const handleSubOption = (option) => {
    setMessages(prev => [...prev, { role: 'user', content: option.label }]);
    setChatPhase('recommendation');

    const r = option.rec;
    const additionalList = r.additional.map(a => `• **${a.name}** — ${a.note}`).join('\n');

    const recMessage = `${r.bryanLine}\n\n**Primary Recommendation**\n**${r.primary.name}**\n\n_Why:_ ${r.primary.why}\n\n**Additional options:**\n${additionalList}`;

    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: recMessage,
        quickReplies: [
          { text: "Tell me more", action: 'followup' },
          { text: "What about cost?", action: 'cost' },
        ],
      }]);
    }, 600);
  };

  const handleQuickReply = (action, text) => {
    setMessages(prev => [...prev, { role: 'user', content: text }]);

    if (action === 'followup') {
      const rec = GOAL_RECS[selectedGoal];
      if (rec) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: rec.followUp,
            quickReplies: [],
          }]);
          setChatPhase('freetext');
        }, 400);
      }
      return;
    }

    if (action === 'cost') {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Pricing depends on which therapies fit your profile. Bryan can give you exact costs and walk you through options — no pressure, just information.",
          quickReplies: [
            { text: "Text Bryan", action: 'text_bryan' },
            { text: "Book a Call", action: 'schedule_call' },
          ],
        }]);
      }, 400);
      return;
    }

    if (action === 'forecast') {
      navigate('/forecast' + (selectedGoal ? `?goal=${selectedGoal}` : ''));
      return;
    }

    if (action === 'text_bryan') {
      window.open('sms:8016518363', '_self');
      return;
    }

    if (action === 'schedule_call') {
      window.open('https://calendly.com/bryanm8623/30min', '_blank');
      return;
    }

    if (action === 'restart') {
      setSelectedGoal(null);
      setChatPhase('welcome');
      setMessages([getWelcomeMessage()]);
      return;
    }

    // Default: send to AI
    handleSend(text);
  };

  const addClosingCTA = () => {
    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Want a deeper recommendation?",
        quickReplies: [
          { text: "Build My AI Forecast", action: 'forecast' },
          { text: "Text Bryan", action: 'text_bryan' },
          { text: "Book a Call", action: 'schedule_call' },
          { text: "Start Over", action: 'restart' },
        ],
      }]);
    }, 800);
  };

  const handleSend = async (messageText = null) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || isLoading) return;
    setInput('');

    if (!messageText) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }

    // Check for intimacy keywords
    const lower = userMessage.toLowerCase();
    if (lower.includes('libido') || lower.includes('intimacy') || lower.includes('sexual') || lower.includes('desire') || lower.includes('ed ') || lower.includes('erectile')) {
      setSelectedGoal('intimacy');
      const rec = GOAL_RECS.intimacy;
      const additionalList = rec.additional.map(a => `• **${a.name}** — ${a.note}`).join('\n');

      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Based on what you've described, Bryan would probably start here:\n\n**Primary Recommendation**\n**${rec.primary.name}**\n\n_Why:_ ${rec.primary.why}\n\n**Additional options:**\n${additionalList}`,
          quickReplies: [
            { text: "Text Bryan", action: 'text_bryan' },
            { text: "Book a Call", action: 'schedule_call' },
          ],
        }]);
      }, 600);

      setTimeout(addClosingCTA, 2000);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        conversation_history: messages.filter(m => !m.quickReplies && !m.goalButtons),
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
      }]);

      addClosingCTA();
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble right now. Let me connect you with Bryan directly.",
        quickReplies: [
          { text: "Text Bryan", action: 'text_bryan' },
          { text: "Book a Call", action: 'schedule_call' },
        ],
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 rounded-full shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)] flex items-center justify-center z-[100] transition-all hover:scale-110 active:scale-95"
            data-testid="chat-open-btn"
          >
            <MessageCircle className="h-7 w-7 text-white" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 w-[90vw] sm:w-[400px] h-[600px] bg-slate-950/95 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.3)] flex flex-col z-[100]"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">AI Health Strategist</h3>
                  <p className="text-xs text-cyan-400">Powered by Bryan 2.0</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="h-5 w-5 text-slate-400 hover:text-white" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.map((message, idx) => (
                <div key={idx}>
                  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 text-white'
                          : 'bg-slate-900/60 border border-slate-800 text-slate-200'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content.split(/(\*\*.*?\*\*|_.*?_)/).map((part, i) => {
                          if (part.startsWith('**') && part.endsWith('**'))
                            return <strong key={i} className="text-white font-bold">{part.slice(2, -2)}</strong>;
                          if (part.startsWith('_') && part.endsWith('_'))
                            return <em key={i} className="text-slate-400">{part.slice(1, -1)}</em>;
                          return part;
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Goal Buttons */}
                  {message.goalButtons && (
                    <div className="grid grid-cols-1 gap-2 mt-3">
                      {GOAL_BUTTONS.map(goal => {
                        const Icon = goal.icon;
                        return (
                          <button
                            key={goal.id}
                            onClick={() => handleGoalSelect(goal.id)}
                            className="flex items-center gap-3 px-4 py-3 bg-slate-900/60 border border-cyan-500/20 hover:border-cyan-500/50 hover:bg-cyan-500/10 rounded-xl text-sm text-slate-300 hover:text-white transition-all text-left"
                            data-testid={`chat-goal-${goal.id}`}
                          >
                            <Icon className="h-5 w-5 text-cyan-500 shrink-0" />
                            <span className="font-medium">{goal.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Sub-flow Options (Recovery path) */}
                  {message.subOptions && (
                    <div className="grid grid-cols-1 gap-2 mt-3">
                      {message.subOptions.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => handleSubOption(opt)}
                          className="px-4 py-3 bg-slate-900/60 border border-orange-500/20 hover:border-orange-500/50 hover:bg-orange-500/10 rounded-xl text-sm text-slate-300 hover:text-white transition-all text-left"
                          data-testid={`chat-sub-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Quick Reply Buttons */}
                  {message.quickReplies && message.quickReplies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 ml-2">
                      {message.quickReplies.map((reply, replyIdx) => (
                        <button
                          key={replyIdx}
                          onClick={() => handleQuickReply(reply.action, reply.text)}
                          className="px-4 py-2.5 bg-slate-900/60 border border-cyan-500/30 hover:border-cyan-500/50 hover:bg-cyan-500/10 rounded-xl text-sm text-slate-300 hover:text-cyan-300 transition-all"
                          data-testid={`chat-reply-${reply.action}`}
                        >
                          {reply.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3">
                    <Loader2 className="h-4 w-4 text-cyan-400 animate-spin" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-cyan-500/20">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Or type a question..."
                  className="flex-1 bg-slate-900/60 border border-slate-800 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isLoading}
                  className="bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl px-4 h-auto"
                  data-testid="chat-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-600 mt-2 text-center">
                AI responses are for informational purposes only
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatAssistant;
